import React, { useMemo } from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  type TooltipContentProps,
  type TooltipValueType,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from '~/timeInit';

import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import type { CppPartner, CppRateCard, CppRateCardStatus } from '~/services/LuxLoyalty';

import { formatCpp } from './cppFormat';

/** The rolling timeline domain: 6 months before today .. 6 months after today (12 months total). */
interface Domain {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  /** Total span of the domain in days — the numeric XAxis runs [0, totalDays]. */
  totalDays: number;
}

/**
 * Computes the rolling 12-month timeline domain centered on today: [today − 6 months,
 * today + 6 months]. Computed at render time so it always tracks the current date. The whole
 * chart works in days-from-`start`, so the XAxis domain is [0, totalDays].
 */
function computeDomain(): Domain {
  const start = dayjs().subtract(6, 'month').startOf('day');
  const end = dayjs().add(6, 'month').endOf('day');
  return { start, end, totalDays: end.diff(start, 'day') };
}

/** Status color encoding — validated (CVD ΔE 10.7, contrast ≥3:1 both modes). Do not change. */
const STATUS_COLORS = {
  ACTIVE: '#0ca30c',
  INACTIVE: '#898781',
} as const;

/** Muted ink used for recessive axis ticks/labels in both light and dark mode. */
const AXIS_MUTED_COLOR = '#898781';

/**
 * Resolves the validated status color for a timeline row. Only ever called with ACTIVE
 * or INACTIVE cards (DRAFT is filtered out in `buildRows`), but falls back to the
 * INACTIVE color defensively rather than widening the lookup type to `any`.
 */
function statusColor(status: CppRateCardStatus): string {
  return status === 'ACTIVE' ? STATUS_COLORS.ACTIVE : STATUS_COLORS.INACTIVE;
}

/** One drawn row of the timeline: the card plus its label and clipped/real windows. */
interface TimelineRow {
  card: CppRateCard;
  label: string;
  /** Resolved partner name (or raw partnerId fallback), kept separately from `label` for the tooltip. */
  partnerName: string;
  /**
   * Days from the domain start to the (clipped) start — the invisible lead-in bar's length.
   * The whole scale is expressed in days-from-domain-start (a small, 0-based number) rather than
   * absolute epoch-ms, so the 0-rooted stacked bars and the XAxis's `[0, totalDays]` domain share
   * one consistent scale (absolute epoch-ms fought recharts' 0-based stacked-bar scaling).
   */
  offsetDays: number;
  /**
   * Visible bar length, in days. Effective windows are inclusive of both ends, so this is the
   * clipped window's day-diff + 1 (a same-day window still spans one day), clamped so the bar
   * never extends past the domain end.
   */
  durationDays: number;
  /** True effective-start (unclipped) for the tooltip. */
  realStart: string;
  /** True effective-end (unclipped), or null if open-ended, for the tooltip. */
  realEnd: string | null;
}

/**
 * Builds the sorted, clipped, filtered set of timeline rows from the raw rate cards.
 *
 * Only ACTIVE/INACTIVE cards are included (DRAFT is excluded). Each surviving card's
 * effective window is clipped to the rolling 12-month domain; an open-ended
 * `effectiveEnd` (null) is treated as extending to the domain end. Cards whose window
 * falls entirely outside the domain are dropped.
 */
function buildRows(
  cards: Array<CppRateCard>,
  partnersById: Map<string, CppPartner>,
  domain: Domain,
): Array<TimelineRow> {
  const rows: Array<TimelineRow> = [];

  for (const card of cards) {
    if (card.status !== 'ACTIVE' && card.status !== 'INACTIVE') {
      continue;
    }

    const start = dayjs(card.effectiveStart);
    // Open-ended cards extend to the domain end for drawing purposes.
    const end = card.effectiveEnd ? dayjs(card.effectiveEnd) : domain.end;

    // Fully outside the domain window - drop it.
    if (end.isBefore(domain.start) || start.isAfter(domain.end)) {
      continue;
    }

    const clippedStart = start.isBefore(domain.start) ? domain.start : start;
    const clippedEnd = end.isAfter(domain.end) ? domain.end : end;

    const partnerName = partnersById.get(card.partnerId)?.name ?? card.partnerId;

    const offsetDays = Math.max(clippedStart.diff(domain.start, 'day'), 0);
    // Effective windows are inclusive of BOTH ends, so the end day itself counts: a
    // Jan 10 -> Jan 12 window spans 3 days, not 2 (and a same-day window spans 1, not 0).
    // Hence the +1 on the day-diff. Clamp the bar's far edge to the domain end so a card that
    // runs to or past the rolling window doesn't overflow the [0, totalDays] axis.
    const durationDays = Math.min(clippedEnd.diff(clippedStart, 'day') + 1, domain.totalDays - offsetDays);

    rows.push({
      card,
      label: `${partnerName} · ${card.reference}`,
      partnerName,
      offsetDays,
      durationDays,
      realStart: card.effectiveStart,
      realEnd: card.effectiveEnd,
    });
  }

  // Group by partner name (asc), then by effective start (asc) within each partner.
  rows.sort((a, b) => {
    const partnerA = partnersById.get(a.card.partnerId)?.name ?? a.card.partnerId;
    const partnerB = partnersById.get(b.card.partnerId)?.name ?? b.card.partnerId;
    if (partnerA !== partnerB) {
      return partnerA.localeCompare(partnerB);
    }
    return dayjs(a.card.effectiveStart).valueOf() - dayjs(b.card.effectiveStart).valueOf();
  });

  return rows;
}

/**
 * Monthly tick marks as day-offsets from the domain start (the axis is in days). Each tick is the
 * day-offset of a month boundary; only ticks within [0, totalDays] are kept.
 */
function buildTicks(domain: Domain): Array<number> {
  const ticks: Array<number> = [];
  let cursor = domain.start.add(1, 'month').startOf('month');
  while (cursor.isBefore(domain.end)) {
    ticks.push(cursor.diff(domain.start, 'day'));
    cursor = cursor.add(1, 'month');
  }
  return ticks;
}

/** Formats a day-offset X-axis tick (days from the domain start) as a short month/year label. */
function formatTick(dayOffset: number, domain: Domain): string {
  return domain.start.add(dayOffset, 'day').format('MMM YY');
}

/** Shape of the data handed to recharts for each row (offset + visible duration, stacked). */
interface ChartDatum {
  label: string;
  offsetDays: number;
  durationDays: number;
  row: TimelineRow;
}

/**
 * Custom tooltip content for the timeline. Shows the partner, card reference, status
 * (in words, not just color), and the REAL (unclipped) effective window - not the
 * window as drawn after clipping to the rolling 12-month domain.
 */
function TimelineTooltip({ active, payload }: TooltipContentProps<TooltipValueType, string>) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // The duration bar carries the row's full datum in its payload; the invisible
  // offset bar is excluded from the tooltip via its own `content={null}`... but since
  // we share one Tooltip across the chart, filter to the entry with dataKey 'durationDays'.
  const entry = payload.find((item) => item.dataKey === 'durationDays');
  if (!entry) {
    return null;
  }
  const datum = entry.payload as ChartDatum;
  const { card, realStart, realEnd, partnerName } = datum.row;
  const statusLabel = card.status === 'ACTIVE' ? 'Active' : 'Deactivated';
  const windowLabel = `${dayjs(realStart).format('DD MMM YYYY')} → ${
    realEnd ? dayjs(realEnd).format('DD MMM YYYY') : 'Ongoing'
  }`;

  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1 }}>
      <Typography variant="subtitle2">{partnerName}</Typography>
      <Typography variant="body2" color="text.secondary">
        {card.reference}
      </Typography>
      <Typography variant="body2">Status: {statusLabel}</Typography>
      <Typography variant="body2">{windowLabel}</Typography>
      <Typography variant="body2">CPP: {formatCpp(card)}</Typography>
    </Paper>
  );
}

/** A small manual legend entry: a colored swatch plus a text label. */
function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * Horizontal (Gantt-style) timeline showing which CPP rate card is effective over which
 * time window, clipped to the rolling 12-month window. One row per ACTIVE/INACTIVE card (DRAFT
 * cards are excluded since they have no meaningful effective window yet).
 *
 * Implemented as a stacked horizontal BarChart: each row draws an invisible "offset" bar
 * (from the domain start to the card's clipped start) followed by the visible colored
 * "duration" bar - recharts has no native Gantt primitive, so this is the standard trick.
 */
export default function RateCardTimeline({
  cards,
  partnersById,
}: {
  cards: Array<CppRateCard>;
  partnersById: Map<string, CppPartner>;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const gridColor = isDark ? '#2c2c2a' : '#e1e0d9';

  const domain = useMemo(() => computeDomain(), []);
  const rows = useMemo(() => buildRows(cards, partnersById, domain), [cards, partnersById, domain]);
  const ticks = useMemo(() => buildTicks(domain), [domain]);

  const chartData: Array<ChartDatum> = useMemo(
    () =>
      rows.map((row) => ({
        label: row.label,
        offsetDays: row.offsetDays,
        durationDays: row.durationDays,
        row,
      })),
    [rows],
  );

  const chartHeight = Math.max(160, rows.length * 36 + 60);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Rate Card Timeline (last 6 months – next 6 months)</Typography>
        {rows.length > 0 && (
          <Stack direction="row" spacing={2}>
            <LegendSwatch color={STATUS_COLORS.ACTIVE} label="Active" />
            <LegendSwatch color={STATUS_COLORS.INACTIVE} label="Deactivated" />
          </Stack>
        )}
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No active or deactivated cards in the last 6 months – next 6 months
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            barCategoryGap="30%"
            margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
          >
            <CartesianGrid horizontal={false} stroke={gridColor} />
            <XAxis
              type="number"
              domain={[0, domain.totalDays]}
              ticks={ticks}
              tickFormatter={(value: number) => formatTick(value, domain)}
              tick={{ fill: AXIS_MUTED_COLOR, fontSize: 11 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={220}
              tick={{ fill: AXIS_MUTED_COLOR, fontSize: 11 }}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
            />
            <Tooltip
              content={(props) => <TimelineTooltip {...props} />}
              cursor={{ fill: isDark ? '#2c2c2a' : '#e1e0d9', opacity: 0.4 }}
            />
            {/* Invisible lead-in bar: pushes the visible bar to its clipped start. No tooltip/legend entry. */}
            <Bar
              dataKey="offsetDays"
              stackId="timeline"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
            />
            {/* Visible duration bar, colored per-row by status via Cell (status isn't a single static fill). */}
            <Bar dataKey="durationDays" stackId="timeline" isAnimationActive={false} radius={[4, 4, 4, 4]} barSize={18}>
              {chartData.map((datum) => (
                <Cell key={datum.row.card.id} fill={statusColor(datum.row.card.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}
